// react
import { ChangeEvent, Dispatch, SetStateAction, useContext, useState, useEffect } from 'react';

// i18n

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';
import { ContextSpendPlan } from '../../pages/ProjectV2/ProjectV2.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';
import { useScrollToError } from '../../../hooks/useScrollToError';

// mui
import {
	Alert,
	Box,
	FormControl,
	FormHelperText,
	Grid,
	InputAdornment,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

// icons
import { AccountTree as IconActivity, Description as IconContract } from '@mui/icons-material';

// types
import { Contract, PlanningActivity, V2ContractPayload, V2Contract } from '../../../@types';

// components
import {
	CustomFieldsFormSection,
	validateCustomFields,
} from '../../ui/CustomFieldsFormSection/CustomFieldsFormSection';
import { CommitmentTypeSelector } from '../../ui/CommitmentTypeSelector/CommitmentTypeSelector';

// utils
import { formatCurrency } from '../../../utils/currency';
import {
	getV1ContractAmountForCurrency,
	getV1ContractSupplierName,
	getContractCommitmentAmount,
} from '../../pages/ProjectV2/Contracts/Contracts.utils';

// Props & Types

export interface FormLinkContractValues {
	activityId: number | '';
	commitmentAmount: string;
	commitmentAmountLocal: string;
	contractId: number | '';
	num: string;
}

export interface FormLinkContractProps {
	contract?: V2Contract | null;
	onClose?: () => void;
	onSuccess?: () => void;
	preSelectedActivityId?: number;
	preSelectedContractId?: number;
	setIsLoading?: Dispatch<SetStateAction<boolean>>;
}

// Component

export const FormLinkContract = ({
	contract,
	onClose,
	onSuccess,
	preSelectedActivityId,
	preSelectedContractId,
	setIsLoading,
}: FormLinkContractProps) => {
	// hooks

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const {
		activities,
		contracts: v2Contracts,
		createContract,
		updateContract,
	} = useContext(ContextSpendPlan);

	// derived
	const isEditMode = Boolean(contract);
	const formId = isEditMode ? 'form-edit-contract' : 'form-link-contract';

	// state - form values
	const [values, setValues] = useState<FormLinkContractValues>(() => {
		if (contract) {
			return {
				activityId: contract.planningActivity || '',
				commitmentAmount:
					contract.commitmentAmount != null ? String(contract.commitmentAmount) : '',
				commitmentAmountLocal:
					contract.commitmentAmountLocal != null ? String(contract.commitmentAmountLocal) : '',
				contractId: contract.id || '',
				num: contract.num || '',
			};
		}
		return {
			activityId: preSelectedActivityId || '',
			commitmentAmount: '',
			commitmentAmountLocal: '',
			contractId: preSelectedContractId || '',
			num: '',
		};
	});

	// state - radio selection for commitment type
	const [commitmentType, setCommitmentType] = useState<'full' | 'partial'>(
		values.commitmentAmount === '' ? 'full' : 'partial'
	);

	// state - validation errors
	const [errors, setErrors] = useState<Partial<Record<keyof FormLinkContractValues, string>>>({});

	// state - submission error
	const [error, setError] = useState<string | null>(null);

	// scroll to error when it occurs
	const errorRef = useScrollToError(error);

	// state - available contracts
	const [contracts, setContracts] = useState<Contract[]>([]);

	// state - custom fields
	const [customData, setCustomData] = useState<Record<string, unknown>>(contract?.customData || {});

	const handleCustomFieldChange = (columnId: string, value: unknown) => {
		setCustomData((prev) => ({ ...prev, [columnId]: value }));
	};

	const customContractColumns =
		(project?.customFieldDefinitions || []).find((d) => d.entityType === 'contract')?.definitions ||
		[];

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch V1 contracts for this project
	const { isLoading: isLoadingContracts } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) {
				setContracts(res.data);
			}
		},
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		url: `${process.env.REACT_APP_API_URL}/contracts?project=${project?.id}&populate=supplier`,
	});

	// Helper to get entity name from activity
	const getEntityName = (activity: PlanningActivity): string => {
		return typeof activity.entity === 'number'
			? `Entity #${activity.entity}`
			: activity.entity?.name || 'Unknown';
	};

	// derived - selected activity
	const selectedActivity = values.activityId
		? activities.find((a) => a.id === values.activityId)
		: null;

	// derived - V1 contract IDs that are already linked to activities (from V2 contracts)
	// Story #112: only contracts that have actually been linked to an activity
	// count as "already linked". Unassigned contracts (activityId == null) now
	// appear in v_contract and must remain selectable here.
	const linkedV1ContractIds = v2Contracts
		.filter((v2c) => v2c.planningActivity != null)
		.map((v2c) => v2c.id);

	// derived - available contracts (filter out already linked ones, but keep current one in edit mode)
	const availableContracts = contracts.filter((c) => {
		// In edit mode, always include the currently linked contract so the
		// user can see what's currently selected even if its status changed.
		if (isEditMode && contract && c.id === contract.id) {
			return true;
		}
		// Hide deactivated / deleted contracts from the picker — they are not
		// valid link targets and should not appear in the available list.
		if (c.status === 'DEACTIVATED' || c.status === 'DELETED') {
			return false;
		}
		// Otherwise, only show contracts that are not already linked
		return !linkedV1ContractIds.includes(c.id);
	});

	// derived - selected contract
	const selectedContract = values.contractId
		? contracts.find((c) => c.id === values.contractId)
		: null;

	// derived - project local currency (nominal display only — no FX conversion)
	const localCurrency = project?.currencies?.find((c) => c.type === 'LOCAL')?.currency || null;

	// derived - contract and activity amounts for logic
	let contractAmountForActivity = null;
	const activityCurrency = selectedActivity?.currency || 'USD';
	const activityAmount = selectedActivity?.amount || null;
	if (selectedContract && selectedActivity) {
		contractAmountForActivity = getV1ContractAmountForCurrency(
			selectedContract,
			activityCurrency
		).amount;
	}

	// If contract amount > activity amount, force partial selection and set commitment amount to activity amount
	const mustUsePartial =
		contractAmountForActivity != null &&
		activityAmount != null &&
		contractAmountForActivity > activityAmount;

	// Effect: auto-select partial and set commitment amount if needed
	useEffect(() => {
		if (mustUsePartial && commitmentType !== 'partial') {
			setCommitmentType('partial');
			setValues((prev) => ({
				...prev,
				commitmentAmount: activityAmount ? String(activityAmount) : '',
			}));
		}
		// If switching to a contract/activity where full is allowed, clear commitmentAmount
		if (
			!mustUsePartial &&
			commitmentType === 'partial' &&
			values.commitmentAmount === String(activityAmount)
		) {
			setValues((prev) => ({ ...prev, commitmentAmount: '' }));
		}
		// eslint-disable-next-line
	}, [mustUsePartial, selectedContract, selectedActivity]);

	// Story #112: when the dialog is reopened with a new preSelectedContractId
	// (eg from the "Link" CTA on an Unassigned contract row), sync it into the
	// form. The form is mounted once at the page level, so the lazy useState
	// initializer would otherwise capture only the first value (often undefined).
	useEffect(() => {
		if (isEditMode) return;
		if (preSelectedContractId && values.contractId !== preSelectedContractId) {
			setValues((prev) => ({ ...prev, contractId: preSelectedContractId }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preSelectedContractId]);

	useEffect(() => {
		if (isEditMode) return;
		if (preSelectedActivityId && values.activityId !== preSelectedActivityId) {
			setValues((prev) => ({ ...prev, activityId: preSelectedActivityId }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preSelectedActivityId]);

	// handlers

	const handleChange = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<number | '' | string>
	) => {
		const { name, value } = e.target;
		setValues((prev) => ({
			...prev,
			[name]: value,
		}));
		// Clear error when field is changed
		if (errors[name as keyof FormLinkContractValues]) {
			setErrors((prev) => ({ ...prev, [name]: undefined }));
		}
		// If commitmentAmount is changed, update radio selection
		if (name === 'commitmentAmount') {
			setCommitmentType(value === '' ? 'full' : 'partial');
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		// Validate
		const newErrors: Partial<Record<keyof FormLinkContractValues, string>> = {};

		// Activity is required when linking a contract for the first time. In
		// edit mode an empty selection ("None") is allowed and unlinks the
		// contract from its activity (sends activityId: null to the server).
		const isUnlinking = isEditMode && values.activityId === '';
		if (!isEditMode && !values.activityId) {
			newErrors.activityId = 'Please select an activity';
		}
		if (!values.contractId) {
			newErrors.contractId = 'Please select a contract';
		}

		// Validate commitment amount if partial commitment is selected. Skipped
		// when unlinking — a commitment is meaningless without an activity and
		// is cleared server-side on unlink.
		if (!isUnlinking && commitmentType === 'partial') {
			if (values.commitmentAmount === '') {
				newErrors.commitmentAmount = 'Please enter the partial commitment amount.';
			} else {
				const amount = parseFloat(values.commitmentAmount);
				if (isNaN(amount) || amount <= 0) {
					newErrors.commitmentAmount = 'Amount must be greater than 0';
				} else if (selectedContract) {
					const activityCurrency = selectedActivity?.currency || 'USD';
					const contractAmount = getV1ContractAmountForCurrency(selectedContract, activityCurrency);
					if (amount > contractAmount.amount) {
						newErrors.commitmentAmount = `Cannot exceed contract amount (${formatCurrency({ value: contractAmount.amount, currency: contractAmount.currency })})`;
					}
				}
			}
			// Local commitment is required for partial commitment
			if (localCurrency) {
				if (values.commitmentAmountLocal === '') {
					newErrors.commitmentAmountLocal = `Required when partial commitment is set (in ${localCurrency})`;
				} else {
					const amountLocal = parseFloat(values.commitmentAmountLocal);
					if (isNaN(amountLocal) || amountLocal <= 0) {
						newErrors.commitmentAmountLocal = 'Amount must be greater than 0';
					}
				}
			}
		}

		// Validate total commitments don't exceed activity budget
		if (selectedActivity && values.contractId) {
			const activityBudget = selectedActivity.amount || 0;
			if (activityBudget > 0) {
				// Sum existing commitments for this activity, excluding the current contract in edit mode
				const existingTotal = v2Contracts
					.filter(
						(c) =>
							c.planningActivity === selectedActivity.id && (!isEditMode || c.id !== contract?.id)
					)
					.reduce((sum, c) => {
						// Aggregate across sibling contracts: a contract with no
						// amount in the activity currency contributes 0 to the
						// activity-currency commitment total.
						const commitment = getContractCommitmentAmount(c, {
							currency: selectedActivity.currency || 'USD',
						});
						return sum + (commitment ?? 0);
					}, 0);

				// Determine new contract's commitment
				let newCommitment: number;
				if (values.commitmentAmount !== '') {
					newCommitment = parseFloat(values.commitmentAmount);
				} else if (selectedContract) {
					const activityCurrency = selectedActivity.currency || 'USD';
					const contractAmount = getV1ContractAmountForCurrency(selectedContract, activityCurrency);
					newCommitment = contractAmount.amount;
				} else {
					newCommitment = 0;
				}

				if (!isNaN(newCommitment) && existingTotal + newCommitment > activityBudget) {
					const available = activityBudget - existingTotal;
					newErrors.commitmentAmount = `Total commitments would exceed activity budget (${formatCurrency({ value: activityBudget, currency: selectedActivity.currency || 'USD' })}). Available: ${formatCurrency({ value: available, currency: selectedActivity.currency || 'USD' })}`;
				}
			}
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		// Custom field validation
		const missingFields = validateCustomFields(customContractColumns, customData);
		if (missingFields.length > 0) {
			setError(`Required custom fields: ${missingFields.join(', ')}`);
			return;
		}

		try {
			if (setIsLoading) {
				setIsLoading(true);
			}

			// In edit mode, if the user changed the contract reference number,
			// PATCH the V1 contract row first (V2 PATCH does not accept `num`).
			if (
				isEditMode &&
				contract &&
				values.num.trim() !== '' &&
				values.num.trim() !== (contract.num || '')
			) {
				const v1Res = await fetch(`${process.env.REACT_APP_API_URL}/contracts/${contract.id}`, {
					method: 'PATCH',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ num: values.num.trim() }),
				});
				const v1Json = await v1Res.json();
				if (v1Json?.error) {
					throw new Error(v1Json.error.message || 'Failed to update reference number');
				}
			}

			// Build payload
			const payload: V2ContractPayload = {
				activityId: values.activityId === '' ? null : (values.activityId as number),
				commitmentAmount:
					isUnlinking || values.commitmentAmount === ''
						? null
						: parseFloat(values.commitmentAmount),
				commitmentAmountLocal:
					isUnlinking || values.commitmentAmountLocal === ''
						? null
						: parseFloat(values.commitmentAmountLocal),
				contractId: values.contractId as number,
				customData,
				projectId: project?.id ?? null,
			};

			let res;
			if (isEditMode && contract) {
				// PATCH
				res = await updateContract(contract.id, payload);
			} else {
				// POST
				res = await createContract(payload);
			}

			if (res.error) {
				throw new Error(res.error.message);
			}

			if (onSuccess) {
				onSuccess();
			}

			const selectedContract = contracts.find((c) => c.id === values.contractId);
			const contractLabel = selectedContract?.num || `Contract #${values.contractId}`;

			setBanner({
				hasClose: true,
				message: `Contract "${contractLabel}" ${isEditMode ? 'updated' : 'linked'} successfully`,
				severity: 'success',
			});

			if (onClose) {
				onClose();
			}
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: `Failed to ${isEditMode ? 'update' : 'create'} contract`
			);
		} finally {
			if (setIsLoading) {
				setIsLoading(false);
			}
		}
	};

	const isLoading = isLoadingContracts;

	return (
		<form id={formId} noValidate onSubmit={handleSubmit}>
			{isLoading && <LinearProgress sx={{ mb: 2 }} />}

			<Grid container spacing={3}>
				{/* Activity Selection */}
				<Grid item xs={12}>
					<FormControl error={Boolean(errors.activityId)} fullWidth required={!isEditMode}>
						<InputLabel>Activity</InputLabel>
						<Select
							disabled={Boolean(preSelectedActivityId)}
							label="Activity"
							name="activityId"
							onChange={handleChange}
							startAdornment={
								<InputAdornment position="start">
									<IconActivity color="action" />
								</InputAdornment>
							}
							value={values.activityId}>
							{isEditMode && (
								<MenuItem value="">
									<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
										None (unassign from activity)
									</Typography>
								</MenuItem>
							)}
							{activities.map((activity) => (
								<MenuItem key={activity.id} value={activity.id}>
									<Box>
										<Typography variant="body2">{activity.name}</Typography>
										<Typography variant="body2" color="text.secondary">
											{getEntityName(activity)} •{' '}
											{formatCurrency({
												currency: activity.currency,
												value: activity.amount,
											})}{' '}
											planned
										</Typography>
									</Box>
								</MenuItem>
							))}
						</Select>
						{errors.activityId && <FormHelperText>{errors.activityId}</FormHelperText>}
					</FormControl>
				</Grid>

				{/* Show activity info if selected */}
				{selectedActivity && (
					<Grid item xs={12}>
						<Paper
							elevation={0}
							sx={{
								bgcolor: 'grey.50',
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: 2,
								p: 2,
							}}>
							<Stack direction="row" justifyContent="space-between" alignItems="center">
								<Box>
									<Typography variant="body2" color="text.secondary">
										Activity Budget
									</Typography>
									<Typography variant="h6" sx={{ fontWeight: 600 }}>
										{formatCurrency({
											currency: selectedActivity.currency,
											value: selectedActivity.amount,
										})}
									</Typography>
								</Box>
							</Stack>
						</Paper>
					</Grid>
				)}

				{/* Contract Selection */}
				<Grid item xs={12}>
					<FormControl
						error={Boolean(errors.contractId)}
						fullWidth
						required
						disabled={isEditMode || Boolean(preSelectedContractId)}>
						<InputLabel>Contract</InputLabel>
						<Select
							label="Contract"
							name="contractId"
							onChange={handleChange}
							startAdornment={
								<InputAdornment position="start">
									<IconContract color="action" />
								</InputAdornment>
							}
							value={values.contractId}>
							{availableContracts.map((c) => {
								const supplierName = getV1ContractSupplierName(c);
								// Use selected activity's currency if available, otherwise show first amount
								const activityCurrency = selectedActivity?.currency || 'USD';
								const contractAmount = getV1ContractAmountForCurrency(c, activityCurrency);
								return (
									<MenuItem key={c.id} value={c.id}>
										<Box>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{c.num}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{supplierName} •{' '}
												{formatCurrency({
													value: contractAmount.amount,
													currency: contractAmount.currency,
												})}
											</Typography>
										</Box>
									</MenuItem>
								);
							})}
						</Select>
						{errors.contractId && <FormHelperText>{errors.contractId}</FormHelperText>}
					</FormControl>
				</Grid>

				{/* Edit-only: contract reference number */}
				{isEditMode && (
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Contract Reference Number"
							name="num"
							onChange={handleChange}
							value={values.num}
							helperText="Updates the contract reference (num) on the underlying contract record."
						/>
					</Grid>
				)}

				{/* Selected Contract Details */}
				{selectedContract && (
					<Grid item xs={12}>
						<Paper sx={{ bgcolor: 'background.default', p: 2 }}>
							<Stack spacing={1.5}>
								<Typography variant="subtitle2" color="text.secondary">
									Contract Details
								</Typography>
								<Stack direction="row" justifyContent="space-between">
									<Typography variant="body2">Supplier</Typography>
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										{getV1ContractSupplierName(selectedContract)}
									</Typography>
								</Stack>
								<Stack direction="row" justifyContent="space-between">
									<Typography variant="body2">Amount</Typography>
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										{(() => {
											const activityCurrency = selectedActivity?.currency || 'USD';
											const contractAmount = getV1ContractAmountForCurrency(
												selectedContract,
												activityCurrency
											);
											return formatCurrency({
												value: contractAmount.amount,
												currency: contractAmount.currency,
											});
										})()}
									</Typography>
								</Stack>
							</Stack>
						</Paper>
					</Grid>
				)}

				{/* Commitment Amount — modern card radio selector + amount inputs */}
				{selectedContract && (
					<Grid item xs={12}>
						<Box sx={{ mb: 2 }}>
							<CommitmentTypeSelector
								value={commitmentType}
								onChange={(next) => {
									setCommitmentType(next);
									if (next === 'full') {
										setValues((prev) => ({
											...prev,
											commitmentAmount: '',
											commitmentAmountLocal: '',
										}));
									}
								}}
								mustUsePartial={mustUsePartial}
								disabledReason={
									mustUsePartial
										? 'Contract amount exceeds the activity budget — a partial commitment is required.'
										: undefined
								}
							/>
						</Box>
						{commitmentType === 'partial' && (
							<Grid container spacing={2} sx={{ mt: 0 }}>
								<Grid item xs={12} md={localCurrency ? 6 : 12}>
									<TextField
										error={Boolean(errors.commitmentAmount)}
										fullWidth
										helperText={errors.commitmentAmount || 'Enter the partial amount to commit.'}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													{selectedActivity?.currency || 'USD'}
												</InputAdornment>
											),
										}}
										label="Amount"
										name="commitmentAmount"
										onChange={handleChange}
										placeholder={
											activityAmount
												? String(activityAmount)
												: (() => {
														const activityCurrency = selectedActivity?.currency || 'USD';
														const contractAmount = getV1ContractAmountForCurrency(
															selectedContract,
															activityCurrency
														);
														return String(contractAmount.amount);
													})()
										}
										type="number"
										value={values.commitmentAmount}
										inputProps={{
											max: mustUsePartial && activityAmount ? activityAmount : undefined,
											min: 0,
											step: 1,
										}}
									/>
								</Grid>
								{localCurrency && (
									<Grid item xs={12} md={6}>
										<TextField
											error={Boolean(errors.commitmentAmountLocal)}
											fullWidth
											helperText={
												errors.commitmentAmountLocal ||
												'Local-currency equivalent (nominal, no FX conversion).'
											}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">{localCurrency}</InputAdornment>
												),
											}}
											label="Amount (Local)"
											name="commitmentAmountLocal"
											onChange={handleChange}
											required={commitmentType === 'partial'}
											type="number"
											value={values.commitmentAmountLocal}
										/>
									</Grid>
								)}
							</Grid>
						)}
					</Grid>
				)}

				{/* Custom Fields */}
				{customContractColumns.filter((c) => c.isActive).length > 0 && (
					<Grid item xs={12}>
						<Paper elevation={0} sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2.5 }}>
							<Grid container spacing={2}>
								<CustomFieldsFormSection
									columns={customContractColumns}
									customData={customData}
									onChange={handleCustomFieldChange}
									title="Additional Information"
								/>
							</Grid>
						</Paper>
					</Grid>
				)}

				{/* Help text */}
				<Grid item xs={12}>
					<Alert severity="info">
						Link an existing contract to this activity to enable payment tracking and
						condition-based payments.
					</Alert>
				</Grid>
			</Grid>

			{error && (
				<Alert ref={errorRef} severity="error" sx={{ mt: 2 }}>
					{error}
				</Alert>
			)}
		</form>
	);
};
